import { useEffect, useMemo, useState } from 'react';

import {
  Box,
  Divider,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  chakra,
} from '@chakra-ui/react';
import { Button, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@mesh.ai/rabbit';
import { Options as RRuleOptions, RRule } from 'rrule';

import { TextBodyMedium, TextHeader3 } from 'uiToolkitV1/Typography';

import {
  generateMonthlyCustomRecurrenceOptions,
  generateWeeklyCustomRecurrenceOptions,
  generateYearlyCustomRecurrenceOptions,
  getRecurrenceFrequencyOptions,
} from './helper';

const RabbitInput = chakra(Input, {
  baseStyle: {
    h: '40px',
    w: '100px',
    minW: '100px',
    pl: 0,
    border: 0,
    borderBottom: '1px dashed',
    borderColor: '#333333',
    borderRadius: 0,
    '&:focus,&:hover': {
      borderBottom: '1px solid',
    },
  },
});

type CustomRecurrenceProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (recurrence: string) => void;
  date: Date;
};

export const CustomRecurrence = ({ isOpen, onClose, onConfirm, date }: CustomRecurrenceProps) => {
  const [recurrenceInterval, setRecurrenceInterval] = useState<number | string>(1);
  const RECURRENCE_FREQUENCY_OPTIONS = useMemo(
    () => getRecurrenceFrequencyOptions(Number(recurrenceInterval) || 1),
    [recurrenceInterval]
  );
  const [recurrenceFrequency, setRecurrenceFrequency] = useState(RECURRENCE_FREQUENCY_OPTIONS[0].value.toString());
  const [recurrenceOccurrence, setRecurrenceOccurrence] = useState<string>('');

  // Dynamically compute the 3rd dropdown options based on the selected frequency
  const recurrenceOccurrenceOptions = useMemo(() => {
    if (recurrenceFrequency === RRule.WEEKLY.toString()) {
      return generateWeeklyCustomRecurrenceOptions(date);
    }

    if (recurrenceFrequency === RRule.MONTHLY.toString()) {
      return generateMonthlyCustomRecurrenceOptions(date);
    }

    if (recurrenceFrequency === RRule.YEARLY.toString()) {
      return generateYearlyCustomRecurrenceOptions(date);
    }

    return [];
  }, [date, recurrenceFrequency]);

  // Set default occurrence when options change
  useEffect(() => {
    if (recurrenceOccurrenceOptions.length > 0) {
      setRecurrenceOccurrence(recurrenceOccurrenceOptions[0].value.toString());
    }
  }, [recurrenceOccurrenceOptions]);

  // Construct the RRule string for onConfirm
  const handleConfirm = () => {
    const interval = typeof recurrenceInterval === 'number' ? recurrenceInterval : 1;
    const freq = Number(recurrenceFrequency);

    const rruleOptions: Partial<RRuleOptions> = {
      freq,
      interval,
    };

    if (freq === RRule.WEEKLY) {
      const selectedOption = recurrenceOccurrenceOptions.find((opt) => opt.value.toString() === recurrenceOccurrence);
      if (selectedOption) rruleOptions.byweekday = [selectedOption.value];
    } else if (freq === RRule.MONTHLY) {
      const selectedOption = recurrenceOccurrenceOptions.find((opt) => opt.value.toString() === recurrenceOccurrence);
      if (selectedOption) {
        if ('isByMonthDay' in selectedOption && selectedOption.isByMonthDay) {
          rruleOptions.bymonthday = Number(recurrenceOccurrence);
        } else {
          rruleOptions.byweekday = [selectedOption.value];
        }
      }
    } else if (freq === RRule.YEARLY) {
      const [day, month] = recurrenceOccurrence.split('-').map(Number);
      if (!Number.isNaN(day) && !Number.isNaN(month)) {
        rruleOptions.bymonth = month;
        rruleOptions.bymonthday = day;
      }
    }

    const rrule = new RRule(rruleOptions).toString().split('RRULE:')[1];
    onConfirm(rrule);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} returnFocusOnClose={false} isCentered autoFocus={false}>
      <ModalOverlay />
      <ModalContent minW="600px" overflow="hidden">
        <ModalHeader p="12px">
          <Box p="8px">
            <TextHeader3 bold>Select recurrence frequency</TextHeader3>
          </Box>
        </ModalHeader>
        <Divider ml="12px" w="calc(100% - 24px)" color="border.CFD7DF" opacity={1} />
        <ModalBody p="12px">
          <Flex px="8px" pb="16px" gap="4px" flexDir="column">
            <TextBodyMedium py="10px" userSelect="none">
              Repeat every
            </TextBodyMedium>
            <Flex gap="8px" alignItems="center">
              <RabbitInput
                type="number"
                min={1}
                tabIndex={-1}
                value={recurrenceInterval}
                onChange={(e) => {
                  const { value } = e.target;
                  if (value === '') setRecurrenceInterval('');
                  else {
                    const numValue = Number(value);
                    if (!Number.isNaN(numValue) && numValue >= 1) setRecurrenceInterval(numValue);
                  }
                }}
                onBlur={(e) => {
                  const value = Number(e.target.value);
                  if (e.target.value === '' || value < 1 || Number.isNaN(value)) setRecurrenceInterval(1);
                }}
              />
              <Select value={recurrenceFrequency} onValueChange={setRecurrenceFrequency}>
                <SelectTrigger style={{ width: '100px' }} tabIndex={-1}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RECURRENCE_FREQUENCY_OPTIONS.map((option) => (
                    <SelectItem key={option.label} value={option.value.toString()}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <TextBodyMedium userSelect="none">on</TextBodyMedium>
              <Select
                value={recurrenceOccurrence}
                onValueChange={setRecurrenceOccurrence}
                disabled={
                  recurrenceFrequency === RRule.WEEKLY.toString() || recurrenceFrequency === RRule.YEARLY.toString()
                }
              >
                <SelectTrigger style={{ minWidth: '120px' }} tabIndex={-1}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent style={{ minWidth: '300px' }}>
                  {recurrenceOccurrenceOptions.map((option) => (
                    <SelectItem key={option.label} value={option.value.toString()}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Flex>
          </Flex>
        </ModalBody>
        <ModalFooter
          p="16px"
          bg="neutral.F5F7F9"
          borderTop="1px solid"
          borderColor="border.CFD7DF"
          justifyContent="space-between"
        >
          <Button variant="text" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="filled" onClick={handleConfirm}>
            Confirm
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
